import { useState, useRef, useMemo, useEffect } from "react";
import { ReactGrid } from "@silevis/reactgrid";
import { Input, Button } from "@hydra/atom/components";
import { useQuery } from "@tanstack/react-query";
import { SvgIcon, Select, IconButton } from "@/components/common";
import { getCompanies } from "@/api/finance/companyApi";
import { getCashFlowRows, getCashFlowColumns } from "./cash-flow-statement";
import { ReportFilterDrawer } from "../../drawers";
import { getAccountingPeriods } from "@/api/finance/accountingPeriodApi";
import { getCashFlow } from "@/api/finance/reportApi";
import { buildTree, getExpandedRows } from "./cells";
import TableSkeleton from "@/components/common/TableSkeleton";
import appSettings from "@/settings";
import { getAccountingMonths } from "@/utils/report/constants";
import { CustomizedDateRangePicker, CustomDropdown } from "@/components/common";
import { useNavigate } from "react-router-dom";
import { formatApiPayloadDate } from "@/utils/helpers";

function ReportSheetCashFlow({
  accountingPeriod: accountingPeriodData,
  activeCompany,
}) {
  const ref = useRef();
  const [filterText, setFilterText] = useState("");
  const [filter, setFilter] = useState({});
  const [isOpen, setIsOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [rowsExpanded, setRowExpanded] = useState([]);
  const [accountingPeriod, setAccountingPeriod] = useState(null);
  const [filters, setFilters] = useState({});
  const [accountingMonth, setAccountingMonth] = useState(null);
  const [dateRange, setDateRange] = useState([new Date(), new Date()]);
  const navigate = useNavigate();
  const [company, setCompany] = useState({
    label: activeCompany?.name,
    value: activeCompany?.id,
  });

  const onClose = (value) => {
    setIsOpen(value);
  };

  useEffect(() => {
    setFilter({
      ...filter,
      company: { label: activeCompany.name, value: activeCompany.id },
    });
  }, [activeCompany]);

  useEffect(() => {
    setAccountingPeriod(accountingPeriodData);
  }, [accountingPeriodData]);

  const exportExcel = () => {
    const companyId = company?.value;
    const startDate = dateRange?.[0] ? formatApiPayloadDate(dateRange[0]) : "";
    const endDate = dateRange?.[1] ? formatApiPayloadDate(dateRange[1]) : "";
    const hasDateFilters = startDate && endDate;

    let url = `${appSettings.baseUrl}/finance/reports/cash-flow/xls?CompanyId=${companyId}`;

    if (hasDateFilters) {
      url += `&StartDate=${startDate}&EndDate=${endDate}`;
    }

    window.open(url, "_blank", "noopener,noreferrer");
  };

  const { data: getCashFlowData, isLoading } = useQuery(
    ["cash-flow", company?.value, accountingMonth, dateRange],
    () =>
      getCashFlow({
        companyId: company?.value,
        startDate: formatApiPayloadDate(dateRange?.[0]),
        endDate: formatApiPayloadDate(dateRange?.[1]),
      }),
    {
      enabled: Boolean(company?.value),
    }
  );

  const columns = useMemo(
    () => getCashFlowColumns(accountingPeriod),
    [ref?.current?.clientWidth, accountingPeriod]
  );

  useEffect(() => {
    setRows(
      buildTree(
        getCashFlowRows({
          data: {
            data: getCashFlowData?.data || [],
          },
          columns,
        })
      )
    );
  }, [getCashFlowData, accountingPeriod]);

  useEffect(() => {
    setRowExpanded(getExpandedRows(rows));
  }, [rows]);

  const handleChanges = (changes) => {
    const newRows = [...rows];
    changes.forEach((change) => {
      const changeRowIdx = rows.findIndex((el) => el.rowId === change.rowId);
      const changeColumnIdx = columns.findIndex(
        (el) => el.columnId === change.columnId
      );
      newRows[changeRowIdx].cells[changeColumnIdx] = change.newCell;
    });
    setRows(buildTree(newRows));
    setRowExpanded(getExpandedRows(newRows));
  };

  const handleDateRangeChange = (value) => {
    setDateRange(value);
    if (value && value.length === 2 && value[0] && value[1]) {
      const formattedStartDate = format(value[0], "dd-MM-yyyy");
      const formattedEndDate = format(value[1], "dd-MM-yyyy");

      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set("startDate", formattedStartDate);
      newSearchParams.set("endDate", formattedEndDate);

      navigate(`?${newSearchParams.toString()}`, { replace: true });
    }
  };

  const handleAccountingPeriodChange = (value) => {
    setAccountingMonth(value);
    if (value && value.value) {
      const currentYear = new Date().getFullYear();
      const monthNumber = value.value;
      const startOfMonth = new Date(currentYear, monthNumber - 1, 1);
      const endOfMonth = new Date(currentYear, monthNumber, 0);
      setDateRange([startOfMonth, endOfMonth]);
      const formattedStartDate = format(startOfMonth, "dd-MM-yyyy");
      const formattedEndDate = format(endOfMonth, "dd-MM-yyyy");

      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set("startDate", formattedStartDate);
      newSearchParams.set("endDate", formattedEndDate);

      navigate(`?${newSearchParams.toString()}`, {
        replace: true,
      });
    }
  };

  return (
    <div className="table-wrapper report-sheet" ref={ref}>
      <div className="filter-row">
        <div className="filter-left-section">
          <div className="col-filter search-bar-report">
            <Input
              className="input-height-fix"
              value={filterText}
              onChange={setFilterText}
              placeholder="Search by name"
            />
          </div>
          <div className="col-filter general-date-range">
            <CustomizedDateRangePicker
              name="dateRange"
              value={dateRange}
              onChange={handleDateRangeChange}
              required
            />
          </div>
        </div>
        <div className="filter-right-section">
          <div className="col-filter">
            <IconButton
              className="icon-button-with-text"
              onClick={() => exportExcel()}
              // disabled={!isDateRangeValid}
            >
              <SvgIcon icon="excel" />
              <span>Download Excel</span>
            </IconButton>
          </div>

          <div className="col-filter">
            <>
              <IconButton
                className="icon-button-with-text"
                onClick={() => setIsOpen(true)}
                small
                bordered
              >
                <SvgIcon icon="filter-lines" />
              </IconButton>
              <ReportFilterDrawer
                filters={filters}
                setFilters={setFilters}
                size={400}
                isOpen={isOpen}
                onClose={onClose}
              />
            </>
          </div>
        </div>
      </div>
      <div className="filter-row">
        <>
          <div className="col-filter company-col ">
            <Select
              id="accountingMonth"
              name="accountMonth"
              placeholder="Select accounting month"
              queryKey="accounting-month"
              value={accountingMonth}
              onChange={(value) => {
                handleAccountingPeriodChange(value);
              }}
              optionsLoader={getAccountingMonths}
              dataAccessKey="data"
              optionLabelKey="name"
              optionValueKey="id"
              isClearable
              backspaceRemovesValue
              rules="required"
            />
          </div>
          <div className="col-filter company-col">
            <Select
              name="company"
              queryKey="company"
              placeholder="Select company"
              value={company}
              onChange={(value) => setCompany(value)}
              optionsLoader={() => getCompanies()}
              dataAccessKey="data"
              optionLabelKey="name"
              optionValueKey="id"
              isClearable
              backspaceRemovesValue
            />
          </div>
        </>
      </div>
      <div className="react-grid-container">
        {!isLoading ? (
          <ReactGrid
            key={rowsExpanded}
            rows={rowsExpanded}
            columns={columns}
            onCellsChanged={handleChanges}
            enableFillHandle
            enableRangeSelection
          />
        ) : (
          <TableSkeleton />
        )}
      </div>
    </div>
  );
}

export default ReportSheetCashFlow;
